import { useTranslation } from 'next-i18next'
import React from 'react'
import { useWatch } from 'react-hook-form'
import { Linking, Text } from 'react-native'
import { tv } from 'tailwind-variants'

import { useLocaleParts } from '@/hooks/useLocaleParts'
import { getAirpodsTermsPath } from '@/shared/components/flows/AirpodsCampaign'
import { body2 } from '@ui-kit/core/typography'

const styles = tv({
  slots: {
    text: body2({ color: 'black' }),
    title: body2({ color: 'black', bold: true }),
    link: body2({ color: 'black', className: 'underline' }),
  },
})

type DocumentLink = { label: string; url: string }

export const useContractDocumentLinks = (
  showAirpodsTerms: boolean,
  labelKeys: { documents: string; airpodsTerms: string }
) => {
  const { t } = useTranslation()
  const { language } = useLocaleParts()
  const policyPreviewUrl = useWatch({ name: 'policy_preview_url' }) as string | undefined
  const policyDocumentUrl = useWatch({ name: 'policy_document_url' }) as string | undefined
  const contractDocumentsUrl = policyPreviewUrl ?? policyDocumentUrl

  return [
    contractDocumentsUrl ? { label: t(labelKeys.documents), url: contractDocumentsUrl } : null,
    showAirpodsTerms
      ? { label: t(labelKeys.airpodsTerms), url: getAirpodsTermsPath(language) }
      : null,
  ].filter((documentLink): documentLink is DocumentLink => documentLink !== null)
}

export const ContractDocuments = ({ showAirpodsTerms }: { showAirpodsTerms: boolean }) => {
  const { t } = useTranslation()
  const { text, title, link } = styles()
  const links = useContractDocumentLinks(showAirpodsTerms, {
    documents: 'sharedFlows_common:steps.payment.contractDocuments.link',
    airpodsTerms: 'sharedFlows_common:steps.payment.contractDocuments.airpodsTerms',
  })

  return (
    <Text className={text()} testID="contract-documents">
      <Text className={title()}>
        {t('sharedFlows_common:steps.payment.contractDocuments.title')}
      </Text>{' '}
      {t('sharedFlows_common:steps.payment.contractDocuments.text')}
      {links.map((documentLink, index) => (
        <React.Fragment key={documentLink.url}>
          {index > 0 ? ', ' : ' '}
          <Text
            accessibilityRole="link"
            className={link()}
            onPress={() => Linking.openURL(documentLink.url)}
          >
            {documentLink.label}
          </Text>
        </React.Fragment>
      ))}
    </Text>
  )
}
